// Client-side loader for Stripe.js (embedded Checkout).
//
// Stripe.js MUST be served from js.stripe.com (PCI requirement — you can't
// self-host or bundle it), so we inject the script tag directly rather than add
// `@stripe/stripe-js` as a dependency. Returns a Stripe instance for the given
// publishable key, loading the script at most once.

const STRIPE_JS_SRC = 'https://js.stripe.com/v3/';

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('Stripe.js can only load in the browser'));
  if ((window as { Stripe?: unknown }).Stripe) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${STRIPE_JS_SRC}"]`);
    const el = existing ?? document.createElement('script');
    el.addEventListener('load', () => resolve());
    el.addEventListener('error', () => {
      scriptPromise = null;
      reject(new Error('Failed to load Stripe.js (blocked by CSP or network?)'));
    });
    if (!existing) {
      el.src = STRIPE_JS_SRC;
      el.async = true;
      document.head.appendChild(el);
    } else if ((window as { Stripe?: unknown }).Stripe) {
      resolve();
    }
  });
  return scriptPromise;
}

/** Minimal shape of the embedded-checkout bits we use. */
export interface EmbeddedCheckout {
  mount(target: HTMLElement | string): void;
  destroy(): void;
}

/**
 * Result of `stripe.handleNextAction` — the browser half of an SCA/3DS challenge. Exactly one of
 * the two fields is set: `error` when the challenge was not completed (declined, abandoned, or a
 * transport failure — `type`/`code` tell those apart), `paymentIntent` with its post-challenge
 * `status` otherwise. Only the bits lib/upgrade-confirmation classifies are typed.
 *
 * An `error` may also carry the `payment_intent` it failed on. Its `status` is the authoritative
 * record of the outcome, so it is typed here too: a real failed 3DS arrives with a `type` the
 * verdict ladder does not recognize, and the intent's status is what disambiguates it.
 */
export interface HandleNextActionResult {
  error?: { type?: string; code?: string; message?: string; payment_intent?: { status?: string } };
  paymentIntent?: { status?: string };
}

/**
 * One Stripe Element (Payment, Address, …) mounted into a node we own.
 *
 * `on('change')` is how the checkout screen learns the buyer edited their address, which is what
 * re-prices the tax line. Only the events we actually listen to are typed.
 */
export interface StripeElement {
  mount(target: HTMLElement | string): void;
  unmount(): void;
  destroy(): void;
  on(event: 'change', handler: (event: StripeElementChangeEvent) => void): void;
  on(event: 'ready', handler: (event: ExpressCheckoutReadyEvent) => void): void;
  on(
    event: 'availablepaymentmethodschange',
    handler: (event: ExpressCheckoutAvailableMethodsEvent) => void,
  ): void;
  on(event: 'click', handler: (event: ExpressCheckoutClickEvent) => void): void;
  on(event: 'confirm', handler: (event: ExpressCheckoutConfirmEvent) => void): void;
}

/**
 * The Express Checkout Element's `ready` event — which wallets THIS browser can actually offer.
 *
 * It is the only way to know. Apple Pay needs Safari with a provisioned card and our domain
 * registered with Stripe; Google Pay needs a signed-in Chrome profile. So the element renders
 * nothing at all for most visitors, and a heading or divider drawn around it unconditionally leaves
 * a labelled empty space. `availablePaymentMethods` being undefined means "no wallet here".
 */
export interface ExpressCheckoutReadyEvent {
  /**
   * DEPRECATED by Stripe in favour of `availablepaymentmethodschange`, and unusable on its own:
   * it answers once, at ready, and availability can resolve later — the element does not show
   * buttons for a brief period after mounting. Read alongside the change event, never instead of it.
   */
  availablePaymentMethods?: Record<string, boolean | undefined>;
}

/**
 * Availability, whenever it changes — including after load, which `ready` cannot report.
 *
 * The field is `paymentMethods` here and `availablePaymentMethods` on `ready`. Same meaning,
 * different name; Stripe renamed it with the new event.
 */
export interface ExpressCheckoutAvailableMethodsEvent {
  paymentMethods?: Record<string, boolean | undefined>;
}

/**
 * The `click` event, which MUST be resolved in the same tick.
 *
 * The wallet sheet is opened by the browser off a genuine user gesture, so anything awaited before
 * `resolve` loses the gesture and the sheet silently never appears. Whatever the sheet should
 * collect is declared here, not later.
 */
export interface ExpressCheckoutClickEvent {
  expressPaymentType?: string;
  resolve(options?: {
    emailRequired?: boolean;
    phoneNumberRequired?: boolean;
    billingAddressRequired?: boolean;
    shippingAddressRequired?: boolean;
    business?: { name: string };
    lineItems?: Array<{ name: string; amount: number }>;
  }): void;
}

/**
 * The `confirm` event — the buyer authorised the wallet, and the details it collected are here.
 *
 * This arrives INSTEAD of our own form being filled, which is the whole point: the address and email
 * come from the wallet, so they have to be read off the event rather than off component state.
 *
 * `paymentFailed` is what closes the sheet when we decline to go through with it — a price that
 * moved, a missing tax identity. Not calling it leaves the buyer looking at a spinner inside the
 * wallet UI with no way back.
 */
export interface ExpressCheckoutConfirmEvent {
  expressPaymentType?: string;
  billingDetails?: {
    name?: string;
    email?: string;
    address?: {
      country?: string;
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
    };
  };
  /**
   * Closes the sheet with an error. The `reason` is not cosmetic: Apple Pay shows its own wording for
   * `invalid_billing_address`, `invalid_shipping_address` and `invalid_payment_data`, and shows
   * nothing useful for `fail` — so a billing-address problem reported as `fail` leaves the buyer with
   * a generic wallet error and the real explanation stranded on the page behind the sheet.
   */
  paymentFailed(options?: {
    reason?: 'fail' | 'invalid_shipping_address' | 'invalid_billing_address' | 'invalid_payment_data'
      | 'address_unserviceable';
    message?: string;
  }): void;
}

export interface StripeElementChangeEvent {
  complete?: boolean;
  /** AddressElement only — what the buyer has typed so far. */
  value?: {
    address?: {
      country?: string;
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
    };
  };
}

/**
 * An Elements group — the deferred-intent flow, where Elements is created with what the purchase
 * COSTS rather than with a client secret, and the intent is created only when the buyer confirms.
 *
 * That ordering is what makes our own screen possible at all: the tax cannot be known until the
 * buyer has entered an address, and an invoice's tax is fixed the moment Stripe finalizes it. So the
 * form renders first, the intent is created last.
 */
/**
 * How a deferred Elements group is being used. Stripe matches the payment methods it offers, and the
 * mandate it takes, to this — so it is a contract, not a label:
 *
 * - `payment` — one charge, nothing stored. A top-up.
 * - `subscription` — a charge now AND recurring billing after it. Offering `payment` here renders
 *   the one-time shape and can save a method that is not eligible to bill again.
 * - `setup` — nothing to charge, store a card for later. A first period that is free.
 */
export type ElementsMode = 'payment' | 'subscription' | 'setup';

export interface StripeElements {
  create(type: 'payment' | 'address' | 'expressCheckout', options?: Record<string, unknown>): StripeElement;
  getElement(type: 'payment' | 'address' | 'expressCheckout'): StripeElement | null;
  /**
   * Re-price the group.
   *
   * The checkout screen does NOT use this, deliberately — see the rebuild comment there. It applies
   * asynchronously and its only completion signal carries nothing identifying which update it
   * belongs to, so with two re-quotes in flight there is no way to know the group holds the amount
   * you think it does. Kept on the type because it is part of Stripe's surface, not because it is
   * safe to reach for here.
   */
  update(options: { amount?: number; currency?: string; mode?: ElementsMode }): void;
  /** Validates and collects the form. REQUIRED before confirm in the deferred-intent flow. */
  submit(): Promise<{ error?: StripeError }>;
}

export interface StripeError {
  type?: string;
  code?: string;
  message?: string;
  /**
   * The intent the error happened on — whichever kind the confirm was for. A `confirmPayment`
   * failure carries `payment_intent`; a `confirmSetup` failure carries `setup_intent`.
   *
   * Both are modelled because reading only one silently ignores the authoritative record on the
   * other path: a setup that actually succeeded, reported alongside a validation error, would be
   * read as a decline.
   */
  payment_intent?: { status?: string };
  setup_intent?: { status?: string };
}

export interface ConfirmResult {
  error?: StripeError;
  paymentIntent?: { status?: string };
  setupIntent?: { status?: string };
}

export interface StripeInstance {
  initEmbeddedCheckout(opts: { clientSecret: string }): Promise<EmbeddedCheckout>;
  /**
   * Deferred-intent Elements. `mode` decides which confirm call finishes it: 'payment' when there is
   * money to take now, 'setup' when the first period is free but a card must still be stored.
   */
  elements(opts: {
    mode: ElementsMode;
    amount?: number;
    currency: string;
    setupFutureUsage?: 'off_session' | 'on_session';
    paymentMethodTypes?: string[];
    appearance?: Record<string, unknown>;
    loader?: 'auto' | 'always' | 'never';
  }): StripeElements;
  confirmPayment(opts: {
    elements: StripeElements;
    clientSecret: string;
    confirmParams?: Record<string, unknown>;
    redirect?: 'always' | 'if_required';
  }): Promise<ConfirmResult>;
  confirmSetup(opts: {
    elements: StripeElements;
    clientSecret: string;
    confirmParams?: Record<string, unknown>;
    redirect?: 'always' | 'if_required';
  }): Promise<ConfirmResult>;
  handleNextAction(opts: { clientSecret: string }): Promise<HandleNextActionResult>;
  // Side-effect-free read of the intent behind a client secret. Used to settle a challenge whose
  // own return value was inconclusive; shares HandleNextActionResult's shape.
  retrievePaymentIntent(clientSecret: string): Promise<HandleNextActionResult>;
  // The setup sibling, for a buyer coming back from a redirect that stored a card rather than took
  // a payment. Same shape; the status lives on `setupIntent`.
  retrieveSetupIntent(clientSecret: string): Promise<ConfirmResult>;
}

export async function getStripe(publishableKey: string): Promise<StripeInstance> {
  await loadScript();
  const factory = (window as unknown as { Stripe?: (key: string) => StripeInstance }).Stripe;
  if (!factory) throw new Error('Stripe.js loaded but window.Stripe is missing');
  return factory(publishableKey);
}
